import { useRef } from 'react'
import { Typography } from 'antd'
import { useRecoilValue } from 'recoil'
import {
  buildingModeState,
  listPointState,
  selectedAreaState,
  selectedPoint,
  selectedPointDetail
} from 'store/buildingStore'

import ResizePreviewIcon from 'components/icons/ResizePreviewIcon'

/**
 * Preview360 Component
 * A resizable preview window for 360-degree panorama images.
 * This component appears at the bottom of the building detail view and can be resized by dragging.
 */
export default function Preview360() {
  // Get state values from Recoil store
  const { previewMode } = useRecoilValue(buildingModeState)
  const currentSelectedPoint = useRecoilValue(selectedPoint)
  const selectedArea = useRecoilValue(selectedAreaState)
  const pointDetail = useRecoilValue(selectedPointDetail)
  const points = useRecoilValue(listPointState)

  // Reference to the container div for resizing
  const containerRef = useRef<HTMLDivElement>(null)

  // Determine visibility conditions for the preview window and image
  const isVisible = !previewMode && selectedArea && points.length
  const isImageVisible =
    currentSelectedPoint || currentSelectedPoint === pointDetail?.id

  /**
   * Handles the mouse down event on the resize handle
   * Initiates the resizing functionality for the preview window
   */
  const handleMouseDown = () => {
    const container = document.getElementById('detail-building-container')
    const previewContainer = containerRef.current
    const rightSidebar = document.getElementById(
      'detail-building-right-sidebar'
    )

    /**
     * Handles the resize operation during mouse movement
     * Calculates and applies new dimensions while respecting min/max constraints
     * @param e Mouse event containing cursor position
     */
    const resize = (e: MouseEvent) => {
      if (!previewContainer || !container) return

      // Calculate maximum allowed dimensions
      const maxWidth = window.innerWidth * 0.8
      const maxHeight = window.innerHeight * 0.8

      // Calculate new dimensions based on mouse position
      const width =
        container.offsetWidth - e.x - (rightSidebar?.offsetWidth || 0) + 4
      const height = container.offsetHeight - e.y + 4

      // Clamp dimensions within min/max bounds
      const clampedWidth = Math.min(Math.max(width, 240), maxWidth)
      const clampedHeight = Math.min(Math.max(height, 200), maxHeight)

      // Apply new dimensions to the container
      previewContainer.style.left = `-${clampedWidth}px`
      previewContainer.style.width = `${clampedWidth}px`
      previewContainer.style.height = `${clampedHeight}px`
    }

    // Add event listeners for resize tracking
    document.addEventListener('mousemove', resize)
    document.addEventListener(
      'mouseup',
      () => {
        // Clean up event listener on mouse up
        document.removeEventListener('mousemove', resize)
      },
      { once: true }
    )
  }

  return (
    <div
      id="preview-360-container"
      ref={containerRef}
      className={`
        flex flex-col theme-bg-secondary absolute -left-[240px] bottom-0 z-[600]
        min-w-[240px] min-h-[200px]
        max-w-[80vw] max-h-[80vh]
        w-[240px] h-[200px] select-none
        ${isVisible ? '' : 'invisible pointer-events-none'}
      `}
    >
      {/* Resize handle in the top-left corner */}
      <div
        className="absolute top-0 left-1 cursor-nwse-resize"
        onMouseDown={handleMouseDown}
      >
        <ResizePreviewIcon size={13} />
      </div>
      {/* Preview window title */}
      <div className="flex h-10 px-6">
        <Typography className="text-[14px] leading-[21px] font-bold text-white my-auto">
          360プレビュー
        </Typography>
      </div>
      {/* Container for the Krpano viewer */}
      <div className={`w-full h-full ${isImageVisible ? '' : 'hidden'}`}>
        <div id="preview-krpano-container" className="h-full" />
      </div>
    </div>
  )
}
